export const SKU_BOOKING_PRACTICE_ID_QUERY = 'practiceId'
export const SKU_BOOKING_REF_QUERY = 'ref'
export const SKU_BOOKING_SCREENING_QUESTIONNAIRE_RESPONSE_ID_QUERY = 'screening_questionnaire_response_id'

export type SkuBookingQueryParams = {
    practiceId?: string | null
    ref?: string | null
    screeningQuestionnaireResponseId?: string | null
}

/** Given a set of search params, drop everything except a subset to forward to the next route */
export function buildSkuBookingQueryParams(searchParams: {
    [key: string]: string | string[] | undefined
}): SkuBookingQueryParams {
    return {
        ref: searchParams?.ref === 'genai' ? 'genai' : undefined,
        practiceId: typeof searchParams?.practiceId === 'string' ? searchParams.practiceId : undefined,
    }
}

/**
 * Builds a query string for SKU booking routes (`/screen`, `/options`, etc.).
 * Omits null/undefined/empty values.
 */
export function buildSkuBookingQueryString(params?: SkuBookingQueryParams): string {
    if (!params) return ''

    const search = new URLSearchParams()
    if (params.ref) {
        search.set(SKU_BOOKING_REF_QUERY, params.ref)
    }
    if (params.practiceId) {
        search.set(SKU_BOOKING_PRACTICE_ID_QUERY, params.practiceId)
    }

    const queryString = search.toString()
    return queryString ? `?${queryString}` : ''
}

/** Screening response id is only forwarded on SKU `/options` (Mercy post-screen offers). */
function buildSkuOptionsQueryString(params?: SkuBookingQueryParams): string {
    if (!params) return ''

    const search = new URLSearchParams()
    if (params.ref) {
        search.set(SKU_BOOKING_REF_QUERY, params.ref)
    }
    if (params.practiceId) {
        search.set(SKU_BOOKING_PRACTICE_ID_QUERY, params.practiceId)
    }
    const screeningQuestionnaireResponseId = params.screeningQuestionnaireResponseId?.trim()
    if (screeningQuestionnaireResponseId) {
        search.set(SKU_BOOKING_SCREENING_QUESTIONNAIRE_RESPONSE_ID_QUERY, screeningQuestionnaireResponseId)
    }

    const queryString = search.toString()
    return queryString ? `?${queryString}` : ''
}

export const skuBookingRoutes = {
    /**
     * Marketing SKU detail page (full product page).
     * Example: /s/acne-treatment/123
     */
    detail(skuName: string, skuId: string, queryParams?: SkuBookingQueryParams): string {
        return `/s/${skuName}/${skuId}${buildSkuBookingQueryString(queryParams)}`
    },

    /**
     * Screening entry for a SKU booking flow.
     * Example: /s/acne-treatment/123/screen?practiceId=abc&ref=genai
     */
    screen(skuName: string, skuId: string, queryParams?: SkuBookingQueryParams): string {
        return `/s/${skuName}/${skuId}/screen${buildSkuBookingQueryString(queryParams)}`
    },

    /**
     * Visit-type selection for a SKU booking flow.
     * Example: /s/acne-treatment/123/options?practiceId=abc&ref=genai
     * May include `screening_questionnaire_response_id` for Mercy post-screen offers.
     */
    options(skuName: string, skuId: string, queryParams?: SkuBookingQueryParams): string {
        return `/s/${skuName}/${skuId}/options${buildSkuOptionsQueryString(queryParams)}`
    },

    /**
     * Location confirmation before screening for SKUs with a questionnaire.
     * Example: /s/acne-treatment/123/location-confirmation?practiceId=abc&ref=genai
     */
    locationConfirmation(skuName: string, skuId: string, queryParams?: SkuBookingQueryParams): string {
        return `/s/${skuName}/${skuId}/location-confirmation${buildSkuBookingQueryString(queryParams)}`
    },
}
